/**
 * Tracks which alerts have been "seen" (opened/clicked) by the user.
 * Uses localStorage keyed by alert category + date + severity hash.
 *
 * Behaviour:
 * - An alert pulses until the user clicks on it ("seen").
 * - Once seen, it stops pulsing and the visual intensity is dimmed.
 * - It re-pulses only if:
 *     • The priority escalated (lower number = more urgent)
 *     • It's a completely new category
 * - If the alert is the same or less severe the next day, it stays dimmed
 *   for 24 h after the last interaction.
 */

const SEEN_KEY = "alert-seen-map";

interface SeenEntry {
  category: string;
  priority: number;
  date: string;     // YYYY-MM-DD
  seenAt: number;   // timestamp (ms)
}

function load(): SeenEntry[] {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SeenEntry[];
  } catch {
    return [];
  }
}

function save(entries: SeenEntry[]) {
  // Keep only last 30 entries
  const trimmed = entries.slice(-30);
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(trimmed));
  } catch {}
}

export function markAlertSeen(category: string, priority: number) {
  const today = new Date().toISOString().slice(0, 10);
  const entries = load().filter(
    (e) => !(e.category === category && e.date === today)
  );
  entries.push({ category, priority, date: today, seenAt: Date.now() });
  save(entries);
}

/**
 * Returns true if this alert should pulse (i.e. NOT yet seen at this severity).
 */
export function shouldPulse(category: string, priority: number): boolean {
  const entries = load();
  // Find latest entry for this category (any day)
  const match = [...entries]
    .filter((e) => e.category === category)
    .sort((a, b) => b.seenAt - a.seenAt)[0];

  if (!match) return true; // Never seen

  // Re-pulse only if priority escalated (lower = more urgent)
  return priority < match.priority;
}

/**
 * Returns a dim factor (0–1) for the visual intensity of the indicator.
 *   1   = full intensity (never seen / escalation)
 *   0.5 = seen within last 24 h at same or lower severity
 *   0.3 = seen & severity actually decreased since last time
 */
export function getIntensityFactor(category: string, priority: number): number {
  const entries = load();
  const match = [...entries]
    .filter((e) => e.category === category)
    .sort((a, b) => b.seenAt - a.seenAt)[0];

  if (!match) return 1; // Never seen → full intensity

  const hoursSinceSeen = (Date.now() - match.seenAt) / (1000 * 60 * 60);

  // Escalation → full intensity regardless
  if (priority < match.priority) return 1;

  // Same severity, seen recently → dimmed
  if (priority === match.priority && hoursSinceSeen < 24) return 0.5;

  // Severity actually improved → strongly dimmed
  if (priority > match.priority && hoursSinceSeen < 24) return 0.3;

  // More than 24 h ago → full intensity (treat as fresh)
  return 1;
}
